import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { analyzeConversation, Conversation, ConversationAnalysis } from '../services/api';
import { useApiKey } from '../context/ApiKeyContext';
import { isValidAnalysis } from '../utils/analysis';
import AnalysisModal from './AnalysisModal';

interface ConversationCardProps {
  conversation: Conversation;
  onViewDetails: (conversationId: string) => void;
}

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail)) {
      return detail.map((item) => String(item)).join(', ');
    }
    if (error.code === 'ECONNABORTED') {
      return 'Analysis timed out. Please try again with a shorter conversation.';
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Analysis failed. Please try again.';
}

const ConversationCard: React.FC<ConversationCardProps> = ({ conversation, onViewDetails }) => {
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [displayAnalysis, setDisplayAnalysis] = useState<ConversationAnalysis | undefined>(undefined);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { hasKey, openKeyRequiredModal } = useApiKey();
  const queryClient = useQueryClient();

  const storedAnalysis = isValidAnalysis(conversation.analysis) ? conversation.analysis : undefined;
  const resolvedAnalysis = displayAnalysis ?? storedAnalysis;

  const analyzeMutation = useMutation(analyzeConversation, {
    onSuccess: (data) => {
      if (data.analysis && isValidAnalysis(data.analysis)) {
        setDisplayAnalysis(data.analysis);
      }
      setShowAnalysis(true);
      queryClient.invalidateQueries(['conversations']);
    },
    onError: (error) => {
      console.error('Error analyzing conversation:', error);
      setShowAnalysis(false);
      setIsAnalyzing(false);
      setAnalysisError(getErrorMessage(error));
      setShowErrorModal(true);
    },
    onSettled: () => {
      setIsAnalyzing(false);
    },
  });

  const isAnalysisLoading =
    isAnalyzing ||
    analyzeMutation.isLoading ||
    (showAnalysis && !resolvedAnalysis && !analyzeMutation.isError);

  const handleAnalyze = () => {
    if (storedAnalysis) {
      setDisplayAnalysis(storedAnalysis);
      setShowAnalysis(true);
      return;
    }
    if (!hasKey) {
      openKeyRequiredModal();
      return;
    }
    setDisplayAnalysis(undefined);
    setIsAnalyzing(true);
    setShowAnalysis(true);
    analyzeMutation.mutate(conversation.id);
  };

  // Parent list may refresh from Firestore before mutation response sets local state.
  useEffect(() => {
    if (showAnalysis && !analyzeMutation.isLoading && storedAnalysis && !displayAnalysis) {
      setDisplayAnalysis(storedAnalysis);
    }
  }, [showAnalysis, analyzeMutation.isLoading, storedAnalysis, displayAnalysis]);

  const getScoreColor = (score: number) => {
    if (score >= 85) return { color: '#2ecc71', bg: 'rgba(46, 204, 113, 0.15)' };
    if (score >= 70) return { color: '#3498db', bg: 'rgba(52, 152, 219, 0.15)' };
    if (score >= 50) return { color: '#ffffff', bg: 'rgba(222, 184, 135, 0.15)' };
    if (score >= 35) return { color: '#e67e22', bg: 'rgba(230, 126, 34, 0.15)' };
    return { color: '#e74c3c', bg: 'rgba(231, 76, 60, 0.15)' };
  };

  const getMessagePreview = () => {
    if (!conversation.messages || conversation.messages.length === 0) {
      return 'No messages found';
    }

    const userMessages = conversation.messages.filter((msg) => msg.role === 'user');
    if (userMessages.length === 0) return 'No user messages';

    const firstMessage = userMessages[0].content;
    return firstMessage.length > 100
      ? `${firstMessage.substring(0, 100)}...`
      : firstMessage;
  };

  const getFirstMessageDate = () => {
    if (!conversation.messages || conversation.messages.length === 0) {
      return null;
    }

    const firstMessage = conversation.messages[0];
    if (firstMessage.timestamp) {
      const date = new Date(firstMessage.timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    if (conversation.created_at) {
      const date = new Date(conversation.created_at);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    return null;
  };

  return (
    <>
      <div
        style={{
          backgroundColor: 'rgba(245, 245, 220, 0.05)',
          border: '1px solid rgba(245, 245, 220, 0.2)',
          borderLeft: '3px solid #ffffff',
          borderRadius: 0,
          padding: '1.5rem',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(245, 245, 220, 0.08)';
          e.currentTarget.style.borderLeftColor = '#ffffff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(245, 245, 220, 0.05)';
          e.currentTarget.style.borderLeftColor = '#ffffff';
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3
              style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: '#ffffff',
                marginBottom: '0.5rem',
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.05em',
              }}
            >
              {conversation.title}
            </h3>
            <p
              style={{
                color: '#ffffff',
                fontSize: '0.875rem',
                marginBottom: '0.5rem',
                fontFamily: "'Inter', sans-serif",
                lineHeight: '1.5',
              }}
            >
              {getMessagePreview()}
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                fontSize: '0.8125rem',
                color: 'rgba(255, 255, 255, 0.7)',
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.05em',
              }}
            >
              <span>{conversation.messages ? conversation.messages.length : 0} MESSAGES</span>
              {getFirstMessageDate() && (
                <>
                  <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>•</span>
                  <span>{getFirstMessageDate()}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {storedAnalysis && (
              <div
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 0,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  color: getScoreColor(storedAnalysis.overall_score).color,
                  backgroundColor: getScoreColor(storedAnalysis.overall_score).bg,
                  border: `1px solid ${getScoreColor(storedAnalysis.overall_score).color}`,
                  letterSpacing: '0.05em',
                }}
              >
                SCORE: {storedAnalysis.overall_score}/100
              </div>
            )}

            <button
              onClick={() => onViewDetails(conversation.id)}
              style={{
                background: 'transparent',
                border: '1px solid #ffffff',
                color: '#ffffff',
                padding: '0.6rem 1.5rem',
                fontSize: '0.85rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: "'Inter', sans-serif",
                borderRadius: 0,
                textTransform: 'uppercase',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.color = '#000000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#ffffff';
              }}
            >
              Details
            </button>

            <button
              onClick={handleAnalyze}
              disabled={analyzeMutation.isLoading}
              style={{
                background: analyzeMutation.isLoading ? 'rgba(245, 245, 220, 0.5)' : '#ffffff',
                border: '1px solid #ffffff',
                color: '#000000',
                padding: '0.6rem 1.5rem',
                fontSize: '0.85rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                cursor: analyzeMutation.isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: "'Inter', sans-serif",
                borderRadius: 0,
                textTransform: 'uppercase',
                opacity: analyzeMutation.isLoading ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!analyzeMutation.isLoading) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#ffffff';
                }
              }}
              onMouseLeave={(e) => {
                if (!analyzeMutation.isLoading) {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.color = '#000000';
                }
              }}
            >
              {analyzeMutation.isLoading ? 'Analyzing...' : storedAnalysis ? 'View Analysis' : 'Analyze'}
            </button>
          </div>
        </div>
      </div>

      {showAnalysis && (
        <AnalysisModal
          conversation={conversation}
          analysis={resolvedAnalysis}
          isLoading={isAnalysisLoading}
          onClose={() => {
            setShowAnalysis(false);
            setDisplayAnalysis(undefined);
          }}
        />
      )}

      {showErrorModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
        >
          <div
            style={{
              backgroundColor: '#000000',
              border: '2px solid #e74c3c',
              borderRadius: 0,
              padding: '2rem',
              maxWidth: '28rem',
              margin: '1rem',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
              <h3
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  marginBottom: '0.5rem',
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Analysis Failed
              </h3>
              <p
                style={{
                  color: '#ffffff',
                  marginBottom: '1.5rem',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {analysisError || 'There was an error processing your conversation analysis. Please try again.'}
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowErrorModal(false)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #ffffff',
                    color: '#ffffff',
                    padding: '0.6rem 1.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontFamily: "'Inter', sans-serif",
                    borderRadius: 0,
                    textTransform: 'uppercase',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.color = '#000000';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowErrorModal(false);
                    if (!hasKey) {
                      openKeyRequiredModal();
                      return;
                    }
                    setDisplayAnalysis(undefined);
                    setIsAnalyzing(true);
                    setShowAnalysis(true);
                    analyzeMutation.mutate(conversation.id);
                  }}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #ffffff',
                    color: '#000000',
                    padding: '0.6rem 1.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontFamily: "'Inter', sans-serif",
                    borderRadius: 0,
                    textTransform: 'uppercase',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.color = '#000000';
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConversationCard;
